import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  clearCallbackRequestState,
  getCallbackRequestKey,
  parseOAuthCallbackParams,
  persistOAuthSession,
  shouldSkipDuplicateCallback,
} from '@frontend/lib/oauth-callback-handler';
import { POST_AUTH_SERVICE_TARGET_KEY } from '@frontend/config/authPolicyRegistry';

export default function OAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const handleCallback = async () => {
      const parsed = parseOAuthCallbackParams({
        code: searchParams.get('code'),
        state: searchParams.get('state'),
        error: searchParams.get('error'),
        errorDescription: searchParams.get('error_description'),
      });

      if (parsed.kind === 'error') {
        setStatus('error');
        setMessage(parsed.message);
        return;
      }

      if (parsed.kind === 'missing') {
        setStatus('error');
        setMessage('Missing authorization code or state parameter');
        return;
      }

      const callbackRequestKey = getCallbackRequestKey(parsed.code, parsed.state);
      if (shouldSkipDuplicateCallback(sessionStorage, callbackRequestKey)) {
        return;
      }

      try {
        sessionStorage.setItem(callbackRequestKey, 'processing');
        const response = await axios.post('/api/auth/oauth/callback', {
          code: parsed.code,
          state: parsed.state,
        });

        persistOAuthSession(
          { localStorage, sessionStorage },
          callbackRequestKey,
          response.data,
        );

        setStatus('success');
        setMessage('Login successful! Redirecting...');

        const resumeTarget = sessionStorage.getItem(POST_AUTH_SERVICE_TARGET_KEY);
        if (resumeTarget) {
          sessionStorage.removeItem(POST_AUTH_SERVICE_TARGET_KEY);
        }
        localStorage.removeItem('explicit_logout');
        sessionStorage.removeItem('explicit_logout');

        setTimeout(() => {
          window.location.href = resumeTarget || '/dashboard';
        }, 1000);
      } catch (error: unknown) {
        clearCallbackRequestState(sessionStorage, callbackRequestKey);
        setStatus('error');
        setMessage(
          axios.isAxiosError<{ message?: string }>(error)
            ? error.response?.data?.message || 'Authentication failed. Please try again.'
            : 'Authentication failed. Please try again.',
        );
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div className="callback-page">
      <div className="card callback-card">
        {status === 'processing' && (
          <>
            <div className="callback-icon">🔄</div>
            <h1>Completing Sign In...</h1>
            <p className="callback-muted">Please wait while we complete your authentication.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="callback-icon callback-success">✓</div>
            <h1 className="callback-success">Welcome Back!</h1>
            <p className="callback-muted">{message}</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="callback-icon callback-error">✗</div>
            <h1 className="callback-error">Authentication Failed</h1>
            <p className="callback-muted callback-error-text">{message}</p>
            <div className="callback-actions">
              <button onClick={() => navigate('/login')} className="btn btn-primary" type="button">
                Try Again
              </button>
              <button onClick={() => navigate('/')} className="btn btn-secondary" type="button">
                Go Home
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
